import React, { useEffect, useMemo, useState } from 'react'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { Modal } from 'react-bootstrap'
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG'
import { getCSSVariableValue } from '../../../../_metronic/assets/ts/_utils'
import { hexToRGB } from '../../../../utils/HexToRgba'
import VestingDataTable from './VestingDataTable'
import { isoToDate } from '../../../../utils/FormatDate'
import { bottom } from '@popperjs/core'

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend)

type VestingFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Semi_Annually' | 'Annually'

type VestingScheduleData = {
  companyName: string
  grantDate: string | Date
  numberGranted: number
  vestedOptions: number
  cliffDurationMonths: number
  totalVestingDurationMonths: number
  vestingFrequency: VestingFrequency
  vestingTemplateName?: string
}

export type ChartDataPoint = {
  date: string
  vested: number
}

type VestingScheduleChartProps = {
  vestingData: VestingScheduleData
  height?: number
  showToggle?: boolean
  defaultView?: 'chart' | 'table'
  className?: string
}

// Helper function to add days
function addDays(date: Date, days: number): Date {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() + days)
  return newDate
}

// Helper function to add months
function addMonths(date: Date, months: number): Date {
  const newDate = new Date(date)
  const originalDay = newDate.getDate()

  newDate.setMonth(newDate.getMonth() + months)

  // Handle month boundary issues (e.g., Jan 31 + 1 month should be Feb 28/29)
  if (newDate.getDate() !== originalDay) {
    newDate.setDate(0)
  }

  return newDate
}

export function generateVestingData(
  grantDate: Date | string,
  numberGranted: number,
  vestedOptions: number,
  cliffDurationMonths: number,
  totalVestingDurationMonths: number,
  vestingFrequency: VestingFrequency
): ChartDataPoint[] {
  const data: ChartDataPoint[] = []
  const startDate = new Date(grantDate)
  const cliffDate = addMonths(startDate, cliffDurationMonths)
  const endDate = addMonths(startDate, totalVestingDurationMonths)

  // Add grant date with 0 vested options
  data.push({
    date: startDate.toISOString().split('T')[0],
    vested: 0,
  })

  // If there's a cliff period, calculate vested options at cliff
  if (cliffDurationMonths > 0) {
    const cliffVestedOptions = Math.floor(
      (numberGranted * cliffDurationMonths) / totalVestingDurationMonths
    )

    data.push({
      date: cliffDate.toISOString().split('T')[0],
      vested: cliffVestedOptions,
    })
  }

  // Calculate remaining vesting periods after cliff based on frequency
  const remainingMonths = totalVestingDurationMonths - cliffDurationMonths
  let monthsPerPeriod: number

  switch (vestingFrequency) {
    case 'Monthly':
      monthsPerPeriod = 1
      break
    case 'Quarterly':
      monthsPerPeriod = 3
      break
    case 'Semi_Annually':
      monthsPerPeriod = 6
      break
    case 'Annually':
    default:
      monthsPerPeriod = 12
      break
  }

  const numberOfPeriods = Math.floor(remainingMonths / monthsPerPeriod)
  const optionsPerPeriod = Math.floor(
    (numberGranted -
      (cliffDurationMonths > 0
        ? Math.floor((numberGranted * cliffDurationMonths) / totalVestingDurationMonths)
        : 0)) /
      numberOfPeriods
  )
  const cliffVested =
    cliffDurationMonths > 0
      ? Math.floor((numberGranted * cliffDurationMonths) / totalVestingDurationMonths)
      : 0

  // Generate remaining vesting points
  for (let i = 1; i <= numberOfPeriods; i++) {
    const vestingDate = addMonths(cliffDate, i * monthsPerPeriod)

    if (vestingDate > endDate) {
      break
    }

    let cumulativeVested: number
    if (i === numberOfPeriods || vestingDate.getTime() >= endDate.getTime()) {
      cumulativeVested = numberGranted
    } else {
      cumulativeVested = cliffVested + optionsPerPeriod * i
    }

    data.push({
      date: vestingDate.toISOString().split('T')[0],
      vested: cumulativeVested,
    })
  }

  // Ensure final point shows full vesting on end date
  const lastPoint = data[data.length - 1]
  if (lastPoint && new Date(lastPoint.date).getTime() < endDate.getTime()) {
    data.push({
      date: endDate.toISOString().split('T')[0],
      vested: numberGranted,
    })
  } else if (lastPoint && lastPoint.vested < numberGranted) {
    data[data.length - 1] = {
      date: endDate.toISOString().split('T')[0],
      vested: numberGranted,
    }
  }

  // Remove duplicate dates if any
  const uniqueData = data.filter(
    (point, index, self) => index === self.findIndex((p) => p.date === point.date)
  )

  return uniqueData
}

export function VestingScheduleGraphPopup({
  showModal,
  handleClose,
  vestingData,
}: {
  showModal: boolean
  handleClose: () => void
  vestingData: VestingScheduleData
}) {
  return (
    <Modal
      id='kt_modal_vesting_graph'
      show={showModal}
      onHide={handleClose}
      dialogClassName='modal-dialog modal-dialog-centered modal-lg mw-md-800px'
      scrollable
    >
      <div className='d-flex justify-content-between align-items-center px-6 px-md-9 pt-6'>
        <h2 className='fw-bolder text-dark m-0 fs-4 fs-md-3'>Vesting Schedule Graph</h2>
        <button className='btn btn-sm btn-icon btn-active-light-primary' onClick={handleClose}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </button>
      </div>
      <Modal.Body className='p-6 p-md-9'>
        <VestingScheduleChart vestingData={vestingData} />
      </Modal.Body>
    </Modal>
  )
}

export function VestingScheduleChart({
  vestingData,
  height = 350,
  showToggle = true,
  defaultView = 'chart',
  className = '',
}: VestingScheduleChartProps) {
  const [isTableView, setIsTableView] = useState<boolean>(defaultView === 'table')
  const [chartData, setChartData] = useState<ChartDataPoint[]>([])
  const currentDate = new Date()

  useEffect(() => {
    if (
      vestingData.grantDate &&
      vestingData.numberGranted &&
      vestingData.cliffDurationMonths != null &&
      vestingData.totalVestingDurationMonths != null &&
      vestingData.vestingFrequency
    ) {
      try {
        const data = generateVestingData(
          vestingData.grantDate,
          vestingData.numberGranted,
          vestingData.vestedOptions,
          vestingData.cliffDurationMonths,
          vestingData.totalVestingDurationMonths,
          vestingData.vestingFrequency
        )

        setChartData(data)
      } catch (error) {
        console.error('Error generating vesting data:', error)
        setChartData([])
      }
    } else {
      setChartData([])
    }
  }, [vestingData])

  // Calculate next vesting date
  const nextVestingDate = useMemo(() => {
    if (chartData.length === 0) {
      return ''
    }

    const today = new Date()
    const nextVestingPoint = chartData.find((point) => new Date(point.date) > today)

    return nextVestingPoint ? isoToDate(nextVestingPoint.date) : '-'
  }, [chartData])

  const chartJsData: ChartData<'line', number[], string> = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    const splitIndex = chartData.findIndex((point) => point.date > today)

    // Calculate cliff period for highlighting
    const grantDate = new Date(vestingData.grantDate)
    const cliffDate = addMonths(grantDate, vestingData.cliffDurationMonths)
    const cliffEndIndex = chartData.findIndex((point) => new Date(point.date) >= cliffDate)

    return {
      labels: chartData.map((point) => isoToDate(point.date)),
      datasets: [
        {
          label: '',
          data: chartData.map((point) => point.vested),
          borderColor: hexToRGB(getCSSVariableValue('--kt-primary'), 1),
          backgroundColor: hexToRGB(getCSSVariableValue('--kt-primary'), 0.1),
          fill: 'origin',
          stepped: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointBackgroundColor: hexToRGB(getCSSVariableValue('--kt-primary'), 1),
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointStyle: 'circle',
          showLine: true,
          segment: {
            borderDash: (ctx) =>
              ctx.p1DataIndex >= splitIndex && splitIndex !== -1 ? [5, 5] : undefined,
          },
          pointHoverBackgroundColor: hexToRGB(getCSSVariableValue('--kt-primary'), 1),
          pointHoverBorderColor: hexToRGB(getCSSVariableValue('--kt-primary'), 1),
          pointHoverBorderWidth: 1,
        },
      ],
    }
  }, [chartData, vestingData])

  const chartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          display: false,
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 'bold',
            },
          },
        },
        title: {
          display: true,
          text: `Vesting Schedule - ${vestingData.companyName}`,
          font: {
            size: 16,
            weight: 'bold',
          },
          padding: {
            top: 10,
            bottom: 40,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          zIndex: 9999,
          cornerRadius: 8,
          padding: 12,
          position: 'nearest', // Adjust tooltip position to avoid overlap
          callbacks: {
            label: function (tooltipItem) {
              const vestedOptions = tooltipItem.raw as number
              const pointDate = new Date(chartData[tooltipItem.dataIndex].date)
              const grantDate = new Date(vestingData.grantDate)
              const cliffDate = addMonths(grantDate, vestingData.cliffDurationMonths)
              const formattedDate = isoToDate(chartData[tooltipItem.dataIndex].date)
              const isCliffPeriod = pointDate < cliffDate && vestingData.cliffDurationMonths > 0
              return ` ${vestedOptions.toLocaleString()} vested options${
                isCliffPeriod ? ' (Cliff Period)' : ''
              }`
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Vesting Date',
            font: {
              size: 12,
              weight: 'bold',
            },
          },
          grid: {
            color: hexToRGB(getCSSVariableValue('--kt-gray-200'), 0.8),
          },
          ticks: {
            maxRotation: 45,
            font: {
              size: 10,
            },
          },
        },
        y: {
          title: {
            display: true,
            text: 'Vested Options',
            font: {
              size: 12,
              weight: 'bold',
            },
          },
          min: 0,
          max: vestingData.numberGranted,
          grid: {
            color: hexToRGB(getCSSVariableValue('--kt-gray-200'), 0.8),
          },
          ticks: {
            stepSize: Math.ceil(vestingData.numberGranted / 10),
            callback: function (value) {
              return (value as number).toLocaleString()
            },
            font: {
              size: 10,
            },
          },
        },
      },
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
    }),
    [vestingData, chartData]
  )

  if (chartData.length === 0) {
    return (
      <div className={`card border border-gray-300 rounded-2 ${className}`}>
        <div
          className='card-body d-flex align-items-center justify-content-center'
          style={{ height: height }}
        >
          <div className='text-center text-muted'>
            <i className='bi bi-graph-up fs-3x mb-3 d-block'></i>
            <p className='fs-5 fw-semibold'>No vesting data available</p>
            <p className='fs-7'>Complete vesting schedule information required to generate chart</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`card border border-gray-300 rounded-2 ${className}`}>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Vesting Schedule</span>
            <span className='text-muted fw-semibold fs-7'>
              {vestingData.vestingTemplateName || 'Custom vesting schedule'}
            </span>
          </h3>
        </div>
        {showToggle && (
          <div className='card-toolbar'>
            <div className='d-flex align-items-center'>
              <span className='text-muted me-3 fs-7'>Chart</span>
              <div className='form-check form-switch me-2'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={isTableView}
                  onChange={(e) => setIsTableView(e.target.checked)}
                  id='vestingViewToggle'
                />
              </div>
              <span className='text-muted fs-7'>Table</span>
            </div>
          </div>
        )}
      </div>
      <div className='card-body pt-0'>
        <div className='mb-4'>
          <div className='row g-3 pt-2 ps-9'>
            <div className='col-6 col-md-3'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-circle symbol-30px overflow-hidden me-3'>
                  <div className='symbol-label bg-light-primary'>
                    <i className='bi bi-calendar-check text-primary fs-6'></i>
                  </div>
                </div>
                <div className='d-flex flex-column'>
                  <span className='text-gray-600 fs-8'>Grant Date</span>
                  <span className='text-gray-800 fw-bold fs-7'>
                    {typeof vestingData.grantDate === 'string'
                      ? isoToDate(vestingData.grantDate)
                      : isoToDate(vestingData.grantDate.toISOString().split('T')[0])}
                  </span>
                </div>
              </div>
            </div>
            <div className='col-6 col-md-3'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-circle symbol-30px overflow-hidden me-3'>
                  <div className='symbol-label bg-light-success'>
                    <i className='bi bi-check-circle text-success fs-6'></i>
                  </div>
                </div>
                <div className='d-flex flex-column'>
                  <span className='text-gray-600 fs-8'>Vested Options</span>
                  <span className='text-gray-800 fw-bold fs-7'>
                    {vestingData.vestedOptions.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            <div className='col-6 col-md-3'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-circle symbol-30px overflow-hidden me-3'>
                  <div className='symbol-label bg-light-warning'>
                    <i className='bi bi-calendar-event text-warning fs-6'></i>
                  </div>
                </div>
                <div className='d-flex flex-column'>
                  <span className='text-gray-600 fs-8'>Next Vesting Date</span>
                  <span className='text-gray-800 fw-bold fs-7'>{nextVestingDate}</span>
                </div>
              </div>
            </div>
            <div className='col-6 col-md-3'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-circle symbol-30px overflow-hidden me-3'>
                  <div className='symbol-label bg-light-dark'>
                    <i className='bi bi-clock text-dark fs-6'></i>
                  </div>
                </div>
                <div className='d-flex flex-column'>
                  <span className='text-gray-600 fs-8'>Frequency</span>
                  <span className='text-gray-800 fw-bold fs-7'>
                    {vestingData.vestingFrequency.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ height: isTableView ? 'auto' : height }}>
          {isTableView ? (
            <VestingDataTable data={chartData} companyName={vestingData.companyName} />
          ) : (
            <div style={{ position: 'relative', height: '100%' }}>
              <Line
                data={chartJsData}
                options={chartOptions}
                plugins={[
                  {
                    id: 'hoverLine',
                    afterDraw: (chart) => {
                      const ctx = chart.ctx
                      const activeElements = chart.tooltip?.getActiveElements()
                      if (activeElements && activeElements.length > 0) {
                        const activePoint = activeElements[0]
                        const x = activePoint.element.x
                        const y = activePoint.element.y
                        const yAxis = chart.scales.y

                        ctx.save()
                        ctx.beginPath()
                        ctx.setLineDash([5, 5])
                        ctx.moveTo(x, yAxis.bottom)
                        ctx.lineTo(x, y)
                        ctx.lineWidth = 1
                        ctx.strokeStyle = hexToRGB(getCSSVariableValue('--kt-primary'), 0.6)
                        ctx.stroke()
                        ctx.restore()
                      }
                    },
                  },
                  {
                    id: 'todayLine',
                    beforeDatasetsDraw: (chart) => {
                      //changed from afterDraw
                      const ctx = chart.ctx
                      const xScale = chart.scales.x
                      const yScale = chart.scales.y

                      // Get today's date
                      const today = new Date()
                      const todayISO = today.toISOString().split('T')[0]

                      const dataPoints = chartData
                      const labels = chart.data.labels as string[]

                      if (dataPoints.length === 0 || labels.length === 0) return

                      const todayTime = today.getTime()
                      let xPosition = null

                      const firstDate = new Date(dataPoints[0].date)
                      const lastDate = new Date(dataPoints[dataPoints.length - 1].date)

                      if (todayTime >= firstDate.getTime() && todayTime <= lastDate.getTime()) {
                        // Find interpolated position
                        for (let i = 0; i < dataPoints.length - 1; i++) {
                          const currentDate = new Date(dataPoints[i].date)
                          const nextDate = new Date(dataPoints[i + 1].date)

                          if (
                            todayTime >= currentDate.getTime() &&
                            todayTime <= nextDate.getTime()
                          ) {
                            const ratio =
                              (todayTime - currentDate.getTime()) /
                              (nextDate.getTime() - currentDate.getTime())
                            const currentX = xScale.getPixelForValue(i)
                            const nextX = xScale.getPixelForValue(i + 1)
                            xPosition = currentX + (nextX - currentX) * ratio
                            break
                          }
                        }

                        // Exact match with a data point
                        if (!xPosition) {
                          const exactIndex = dataPoints.findIndex((p) => p.date === todayISO)
                          if (exactIndex !== -1) {
                            xPosition = xScale.getPixelForValue(exactIndex)
                          }
                        }

                        if (xPosition) {
                          // Draw line
                          ctx.save()
                          ctx.beginPath()
                          ctx.setLineDash([5, 5])
                          ctx.moveTo(xPosition, yScale.top)
                          ctx.lineTo(xPosition, yScale.bottom)
                          ctx.lineWidth = 2
                          ctx.strokeStyle = '#dc5516ff'
                          ctx.stroke()
                          ctx.restore()

                          // Draw label
                          ctx.save()
                          ctx.fillStyle = '#dc5516ff'
                          ctx.font = 'bold 11px Arial'
                          ctx.textAlign = 'center'
                          ctx.fillText('Today', xPosition, yScale.top - 20)
                          ctx.restore()
                        }
                      }
                    },
                  },
                ]}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
